import { refPort, refCountry } from "@tc/drizzle-v2"
import { eq, and } from "drizzle-orm"
import {
  SQSClient,
  SendMessageCommand,
  MessageAttributeValue,
} from "@aws-sdk/client-sqs"
import { DbTransaction } from "@tc/middy-decorators"
import { UnLocodeDoc } from "../types/reference-data"
import { searchBuilderLog } from "../utils/logger"

const sqs_client = new SQSClient({})
const queue_url = process.env["UN_LOCODE_QUEUE_URL"]

export const getUnLocodeData = async (
  tx: DbTransaction,
  id: string,
): Promise<UnLocodeDoc | undefined> => {
  const [row] = await tx
    .select({
      id: refCountry.itemCode,
      countryCode: refCountry.itemCode,
      countryName: refCountry.itemName,
      portCode: refPort.itemCode,
      portName: refPort.itemName,
    })
    .from(refPort)
    .leftJoin(refCountry, eq(refPort.countryId, refCountry.id))
    .where(and(eq(refPort.id, parseInt(id)), eq(refPort.active, true)))

  if (!row) {
    console.error(`Port with id ${id} not found/active`)
    return
  }

  const unLocodeDoc: UnLocodeDoc = {
    id: row.portCode,
    country_code: row.countryCode ?? null,
    country: row.countryName ?? null,
    location_code: row.portCode.substring(2),
    location: row.portName ?? null,
    code: row.portCode,
  }

  searchBuilderLog.debug({ document: unLocodeDoc })

  return unLocodeDoc
}

export const getUnLocodeBulkData = async (
  tx: DbTransaction,
  ids: readonly string[],
): Promise<Map<string, UnLocodeDoc>> => {
  if (ids.length === 0) return new Map()

  const [countries, allPorts] = await Promise.all([
    tx.select({ id: refCountry.id, itemCode: refCountry.itemCode, itemName: refCountry.itemName }).from(refCountry),
    tx.select({
      id: refPort.id,
      countryId: refPort.countryId,
      portCode: refPort.itemCode,
      portName: refPort.itemName,
      active: refPort.active,
    }).from(refPort).where(eq(refPort.active, true)),
  ])

  const countryMap = new Map(countries.map((c) => [c.id, { code: c.itemCode, name: c.itemName }]))

  const result = new Map<string, UnLocodeDoc>()
  for (const row of allPorts) {
    const country = countryMap.get(row.countryId)
    result.set(row.id.toString(), {
      id: row.portCode,
      country_code: country?.code ?? null,
      country: country?.name ?? null,
      location_code: row.portCode.substring(2),
      location: row.portName ?? null,
      code: row.portCode,
    })
  }

  // Return only requested IDs
  const filtered = new Map<string, UnLocodeDoc>()
  for (const id of ids) {
    const doc = result.get(id)
    if (doc) filtered.set(id, doc)
  }
  return filtered
}

export const saveUnLocodeToSearch = async (
  tx: DbTransaction,
  id: string,
  collectionName: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const doc = await getUnLocodeData(tx, id)

  if (!doc || Object.keys(doc).length === 0) {
    console.error(`Failed to prepare document for id: ${id}`)
    return
  }

  if (options?.returnDoc) return doc as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: collectionName,
      DataType: "String",
    },
  }

  const command = new SendMessageCommand({
    QueueUrl: queue_url,
    MessageBody: JSON.stringify(doc),
    MessageAttributes: attributes,
  })

  await sqs_client.send(command)
}
